import {ContentEditableEditorPlugin, createContentEditableHtmlEditor} from '@frontapp/editor-html';
import {useEditor} from '@frontapp/editor-html/lib/react';
import {Trans} from '@lingui/macro';
import React, {FC, ReactNode, useCallback, useEffect, useState} from 'react';
import styled, {css} from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {SimpleDropdownWithHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownWithHeader';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {createEditorServiceChangeManager} from '../../../../components/common/editor/change/editorServiceChangeManager';
import {ContentEditable} from '../../../../components/common/editor/contentEditable';
import {ServiceSubscription} from '../../../../components/common/editor/serviceSubscription';
import {Paragraph} from '../../../../components/common/paragraph';
import {ReferenceFormatModel} from '../../../../models/api/autolinkerModel';
import {matchFormat} from '../../regexTemplateHelpers';

const HIGHLIGHT_CLASS = 'front-regex-highlight';

export interface RegexPlaygroundProps {
  format: ReferenceFormatModel;
  shouldOnlyMatchFirst?: boolean;
  title: ReactNode;
  description: ReactNode;
}

const StyledSampleDiv = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
  line-height: 20px;

  .${HIGHLIGHT_CLASS} {
    background-color: ${(p) => p.theme.palette.blue.shade30};
  }
`;

const editorStyle = css`
  padding: 12px;
`;

const StyledPlaceholderDiv = styled.div`
  position: absolute;
  ${editorStyle};
  pointer-events: none;
  color: ${(p) => p.theme.greys.shade50};
`;

const StyledContentEditable = styled(ContentEditable)`
  height: 100%;
  ${editorStyle};
`;

const StyledFooter = styled.footer`
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade60};

  padding: 12px 12px 5px 12px;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};

  display: flex;
  justify-content: center;
`;

export const RegexPlayground: FC<RegexPlaygroundProps> = ({
  title,
  description,
  format,
  shouldOnlyMatchFirst,
}) => {
  const [content, setContent] = useState('');

  const isDisabled = !format.pattern;

  const renderModal = useCallback(
    (transferProps: PopoverTransferProps) => (
      <SimpleDropdownWithHeader title={title} width={450} height={300} hasBackground {...transferProps}>
        <RegexPlaygroundEditor
          format={format}
          shouldOnlyMatchFirst={shouldOnlyMatchFirst}
          initialContent={content}
          shouldFocus
          onChange={setContent}
        />
        <StyledFooter>
          <Paragraph>{description}</Paragraph>
        </StyledFooter>
      </SimpleDropdownWithHeader>
    ),
    [format, shouldOnlyMatchFirst, content],
  );

  return (
    <PopoverCoordinator
      renderButton={() => (
        <Button isDisabled={isDisabled} size={VisualSizesEnum.MEDIUM}>
          {title}
        </Button>
      )}
      renderModal={renderModal}
      isDisabled={isDisabled}
    />
  );
};

interface RegexPlaygroundEditorProps {
  format: ReferenceFormatModel;
  shouldOnlyMatchFirst?: boolean;
  initialContent: string;
  onChange: HandlerOf<string>;
  shouldFocus: boolean;
}

const RegexPlaygroundEditor: FC<RegexPlaygroundEditorProps> = ({
  format,
  shouldOnlyMatchFirst,
  initialContent,
  onChange,
  shouldFocus,
}) => {
  const editor = useEditor(
    () =>
      createContentEditableHtmlEditor({
        plugins: [createRegexHighlightPlugin(format, shouldOnlyMatchFirst)],
        initialHtml: initialContent,
        isInitialHtmlClean: true,
        shouldPasteAsText: true,
        hasNewDeleteSelection: true,
        shouldNativeDelete: true,
      }),
    [format, shouldOnlyMatchFirst],
  );

  useEffect(() => {
    const changeManager = createEditorServiceChangeManager(editor.services.html);

    return changeManager.subscribe(() => onChange(editor.services.html.cleanHtml));
  }, [editor, onChange]);

  return (
    <StyledSampleDiv>
      <ServiceSubscription source={editor.services.empty}>
        {({isEmpty}) => isEmpty && renderPlaceholder()}
      </ServiceSubscription>
      <StyledContentEditable editor={editor} shouldFocus={shouldFocus} />
    </StyledSampleDiv>
  );
};

/*
 * Helpers.
 */

function renderPlaceholder() {
  return (
    <StyledPlaceholderDiv>
      <Trans id="try-regex-sample">Insert your test text</Trans>
    </StyledPlaceholderDiv>
  );
}

function createRegexHighlightPlugin(
  format: ReferenceFormatModel,
  shouldOnlyMatchFirst?: boolean,
): ContentEditableEditorPlugin {
  return {
    decorateText: (text) => {
      if (!format.pattern) {
        return [];
      }

      const matches = matchFormat(format, text, shouldOnlyMatchFirst);

      return matches.map((match) => ({
        index: match.index,
        length: match.value.length,
        className: HIGHLIGHT_CLASS,
      }));
    },
  };
}
